import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiBook, FiCalendar, FiMapPin } from 'react-icons/fi';

const EducationSection = styled.section`
  padding: 100px 150px;
  background-color: var(--bg-secondary);
  
  @media (max-width: 768px) {
    padding: 80px 25px;
  }
`;

const EducationContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const EducationTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  &::after {
    content: "";
    display: block;
    width: 300px;
    height: 1px;
    margin-left: 20px;
    background-color: var(--bg-highlight);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::after {
      width: 100px;
    }
  }
`;

const IconContainer = styled(motion.div)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 30px;
  flex-shrink: 0;
  box-shadow: 0 10px 25px rgba(2, 12, 27, 0.2);
  border: 1px solid var(--bg-highlight);
  transition: all 0.3s ease;
  
  svg {
    font-size: 30px;
    color: var(--accent-primary);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const EducationCard = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  padding: 30px;
  margin-bottom: 30px;
  border-radius: 12px;
  background: var(--bg-primary);
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  border: 1px solid var(--bg-highlight);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px -20px rgba(2, 12, 27, 0.7);
    border-color: var(--accent-primary);
    
    ${IconContainer} {
      transform: translateY(-5px) rotate(5deg);
      box-shadow: 0 8px 20px rgba(100, 255, 218, 0.3);
      border-color: var(--accent-primary);
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EducationContent = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 60px;
    left: -35px;
    width: 1px;
    height: calc(100% - 60px);
    background: linear-gradient(to bottom, var(--accent-primary), transparent);
    z-index: 0;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const SchoolName = styled.h3`
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Degree = styled.h4`
  color: var(--accent-primary);
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 16px;
  font-family: var(--font-mono);
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
  color: var(--text-secondary);
  font-family: var(--font-mono);
  font-size: 14px;
  
  svg {
    margin-right: 8px;
  }
  
  .education-date {
    display: flex;
    align-items: center;
    margin-right: 20px;
    margin-bottom: 5px;
  }
  
  .education-location {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }
`;

const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  max-width: 600px;
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.3,
      duration: 0.5
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};

const iconVariants = {
  hidden: { scale: 0, rotate: -180 },
  visible: {
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 20
    }
  }
};

const Education = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [cardRef, cardInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const educationItems = [
    {
      school: "C.M.R. Institute of Technology",
      degree: "Master of Computer Applications",
      date: "September 2019 - August 2021",
      location: "Bengaluru, Karnataka",
      description: "Developed strong foundations in computer applications, focusing on programming and system security. Placed first in Programming Level 5 Hackathon (2020) and CMRIT Cloud Hackathon (2019). Won Inter-College Capture the Flag (CTF) Competition (2020) at St. Joseph's College of Engineering, enhancing practical skills in identifying and addressing security vulnerabilities."
    }
  ];
  
  return (
    <EducationSection id="education">
      <EducationContainer>
        <EducationTitle
          ref={titleRef}
          initial={{ y: 20, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          Education
        </EducationTitle>
        
        <motion.div
          ref={cardRef}
          variants={containerVariants}
          initial="hidden"
          animate={cardInView ? "visible" : "hidden"}
        >
          {educationItems.map((item, index) => (
            <EducationCard
              key={index}
              variants={cardVariants}
            >
              <IconContainer variants={iconVariants}>
                <FiBook />
              </IconContainer>
              
              <EducationContent>
                <SchoolName>{item.school}</SchoolName>
                <Degree>{item.degree}</Degree>
                <InfoRow>
                  <div className="education-date">
                    <FiCalendar />
                    {item.date}
                  </div>
                  <div className="education-location">
                    <FiMapPin />
                    {item.location}
                  </div>
                </InfoRow>
                <Description>{item.description}</Description>
              </EducationContent>
            </EducationCard>
          ))}
        </motion.div>
      </EducationContainer>
    </EducationSection>
  );
};

export default Education; 